import { useCallback, useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Loader2, Pencil, Save, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import {
  getOrCreateHomework,
  homeworkStatusClass,
  homeworkStatusLabel,
  saveHomework,
  submitHomework,
} from "@/lib/grading";

const AUTOSAVE_MS = 2000;

export default function HomeworkPanel({ courseId, chapter, canView }) {
  const [sub, setSub] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const dirtyRef = useRef(false);
  const timerRef = useRef(null);

  // Load / create submission whenever chapter changes
  useEffect(() => {
    if (!canView || !chapter) {
      setSub(null);
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    setError(null);
    getOrCreateHomework(courseId, chapter.id)
      .then((s) => {
        if (!alive) return;
        setSub(s);
        setAnswers(s.answers || []);
      })
      .catch((e) => {
        if (!alive) return;
        const code = e?.response?.data?.detail;
        setError(
          code === "payment_required"
            ? "Complete your payment to unlock homework."
            : code || "Failed to load homework."
        );
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [courseId, chapter, canView]);

  const onChange = (qid, value) => {
    if (sub?.status === "graded") return;
    setAnswers((prev) =>
      prev.map((a) =>
        a.question_id === qid ? { ...a, answer_text: value } : a
      )
    );
    dirtyRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => persistDraft(), AUTOSAVE_MS);
  };

  const persistDraft = useCallback(async () => {
    if (!sub || !dirtyRef.current || sub.status === "graded") return;
    setSaving(true);
    try {
      const updated = await saveHomework(sub.id, answers);
      setSub(updated);
      setAnswers(updated.answers || []);
      setLastSavedAt(new Date());
      dirtyRef.current = false;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Autosave failed");
    } finally {
      setSaving(false);
    }
  }, [sub, answers]);

  const handleSubmit = async () => {
    if (!sub) return;
    if (dirtyRef.current) await persistDraft();
    try {
      const updated = await submitHomework(sub.id);
      setSub(updated);
      window.dispatchEvent(new CustomEvent("kks:rewards-refresh"));
      toast.success("Homework submitted — your teacher will grade it soon.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    }
  };

  if (!canView) {
    return (
      <div className="py-10 text-center text-stone-500 text-sm">
        Complete your payment to unlock homework.
      </div>
    );
  }
  if (loading) {
    return (
      <div className="py-10 text-center text-stone-500 text-sm">
        <Loader2 className="mx-auto h-4 w-4 animate-spin" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="py-6 text-sm text-amber-700 flex items-start gap-2">
        <AlertCircle className="h-4 w-4 mt-0.5" /> {error}
      </div>
    );
  }
  if (!sub) return null;

  const isLocked = sub.status === "graded";
  const showAutosave = sub.status !== "graded";

  return (
    <div className="space-y-4" data-testid="homework-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Pencil className="h-4 w-4 text-amber-600" />
          <span className="font-medium text-sm">{chapter.title} — Homework</span>
          <Badge
            variant="outline"
            className={homeworkStatusClass(sub.status)}
            data-testid="homework-status-badge"
          >
            {homeworkStatusLabel(sub.status)}
            {sub.status === "graded" && sub.awarded_marks != null && (
              <span className="ml-1">
                · {sub.awarded_marks}/{sub.total_marks}
              </span>
            )}
          </Badge>
        </div>
        {showAutosave && (
          <div className="text-xs text-stone-500 flex items-center gap-1">
            {saving ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" /> Saving…
              </>
            ) : lastSavedAt ? (
              <>
                <CheckCircle2 className="h-3 w-3 text-emerald-600" /> Saved
              </>
            ) : (
              <span>Autosaves as you type</span>
            )}
          </div>
        )}
      </div>

      {sub.status === "graded" && sub.teacher_feedback && (
        <div
          className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900"
          data-testid="homework-feedback"
        >
          <div className="text-xs font-medium text-emerald-700 mb-0.5">
            Teacher feedback
          </div>
          {sub.teacher_feedback}
        </div>
      )}

      {answers.length === 0 ? (
        <p className="text-sm text-stone-500">
          No homework questions for this chapter.
        </p>
      ) : (
        <ol className="space-y-3 list-decimal pl-5">
          {answers.map((a) => (
            <li key={a.question_id}>
              <div className="text-sm font-medium text-stone-800 mb-1">
                {a.question_text}
              </div>
              <Textarea
                value={a.answer_text}
                onChange={(e) => onChange(a.question_id, e.target.value)}
                placeholder="Type your answer…"
                disabled={isLocked}
                rows={3}
                data-testid={`homework-answer-${a.question_id}`}
              />
            </li>
          ))}
        </ol>
      )}

      <div className="flex justify-end pt-1">
        {sub.status === "draft" && (
          <Button
            onClick={handleSubmit}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="submit-homework-button"
          >
            <Save className="mr-1 h-4 w-4" /> Submit homework
          </Button>
        )}
        {sub.status === "submitted" && (
          <Button
            onClick={handleSubmit}
            variant="outline"
            data-testid="resubmit-homework-button"
          >
            <Save className="mr-1 h-4 w-4" /> Update & resubmit
          </Button>
        )}
      </div>
    </div>
  );
}
